import styled from '@emotion/styled';
import { NavigationIds } from 'components/Header/navigation';
import { HeaderContainer } from 'components/Header/styled';
import setCssScrollBarWidthCustomVar from 'helpers/setCssScrollBarWidthCustomVar';
import { useNotification } from 'hooks/useNotification';
import noop from 'lodash/noop';
import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router';
import { getAllSingleTrackRouteSegments } from 'services/libraryItem/singleTrackRouting';
import { useScrollPosition } from './useScrollPosition';
import useWebview from './useWebview';
import { Box } from '@chakra-ui/react';
import { FLUX_SPACER_HEIGHT } from 'components/Header/FluxNavigation/constants';
import { USE_FLUX_NAVIGATION } from 'components/Header/FluxNavigation/config';

const Notification = React.lazy(() => import('components/Notification'));
const Header = lazy(() => import('components/Header'));
const HeaderIntl = lazy(() => import('components/HeaderIntl'));
const GUIDED_MEDITATION_PATH_SEGMENTS = new Set(
  getAllSingleTrackRouteSegments()
);
const FluxNavigation = lazy(() => import('components/Header/FluxNavigation'));

interface ProviderProps {
  intlHeader?: boolean;
  children?: React.ReactNode;
}

export const HeaderComponentContext = React.createContext<{
  initialHeight: number;
  scrolledHeight: number;
  stickyHeader: boolean;
  transparentHeader: boolean;
  cookieBannerDisplay: boolean;
  subNavigationOpenId?: NavigationIds;
  isShowSearch: boolean;
  hideHeader: boolean;
  bgColor?: string; // header background color
  showGetStarted: boolean;
  customCtaButton: React.ReactNode | null;
  useGreenCTA: boolean;
  setCookieBannerDisplay: React.Dispatch<React.SetStateAction<boolean>>;
  hideShadow: () => void;
  setStickyHeader: (value: boolean) => void;
  setTransparentHeader: (value: boolean) => void;
  setHideHeader: (value: boolean) => void;
  setSubNavigationOpenId: (value?: NavigationIds) => void;
  setIsShowSearch: (value: boolean) => void;
  setBgColor: (value: string | undefined) => void; // use to set bg color
  setShowGetStarted: (value: boolean) => void;
  setCustomCtaButton: (component: React.ReactNode | null) => void;
  setUseGreenCTA: (value: boolean) => void;
}>({
  initialHeight: 0,
  scrolledHeight: 0,
  stickyHeader: true,
  transparentHeader: false,
  cookieBannerDisplay: false,
  subNavigationOpenId: undefined,
  isShowSearch: false,
  hideHeader: false,
  showGetStarted: true,
  customCtaButton: null,
  useGreenCTA: false,
  setCookieBannerDisplay: noop,
  hideShadow: noop,
  setStickyHeader: noop,
  setTransparentHeader: noop,
  setHideHeader: noop,
  setSubNavigationOpenId: noop,
  setIsShowSearch: noop,
  setBgColor: noop,
  setShowGetStarted: noop,
  setCustomCtaButton: noop,
  setUseGreenCTA: noop
});

export const HeaderSpace = styled.div`
  height: 64px;
  @media (min-width: 1280px) {
    height: 112px;
  }
`;

export const HeaderProvider: React.FC<ProviderProps> = ({
  children,
  intlHeader
}) => {
  const [initialHeight, setInitialHeight] = useState(0);
  const [scrolledHeight, setScrolledHeight] = useState(0);
  const [hideShadow, setHideShadow] = useState(false);
  const [transparent, setTransparent] = useState(false);
  const [sticky, setSticky] = useState(true);
  const [hideHeader, setHideHeader] = useState(false);
  const [cookieBannerDisplay, setCookieBannerDisplay] = useState(false); // Check whether cookies banner display on ui.
  const [subNavigationOpenId, setSubNavigationOpenId] =
    useState<NavigationIds>();
  const [isShowSearch, setIsShowSearch] = useState(false);
  const [bgColor, setBgColor] = useState<string>();
  const [showGetStarted, setShowGetStarted] = useState(true);
  const [customCtaButton, setCustomCtaButton] =
    useState<React.ReactNode | null>(null);
  const [useGreenCTA, setUseGreenCTA] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const deferHeaderForRoute = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length < 3) {
      return false;
    }

    return GUIDED_MEDITATION_PATH_SEGMENTS.has(segments[segments.length - 2]);
  }, [pathname]);
  const [shouldRenderHeader, setShouldRenderHeader] = useState(
    !deferHeaderForRoute
  );
  const headerRef = useRef<HTMLDivElement>(null);
  const { id } = useNotification();
  const { scroll80 } = useScrollPosition(shouldRenderHeader);
  const { isRetreatWebview } = useWebview();

  const handleHideShadow = useCallback(() => setHideShadow(true), []);
  const handleSetSticky = useCallback((value: boolean) => setSticky(value), []);
  const handleSetTransparent = useCallback(
    (value: boolean) => setTransparent(value),
    []
  );

  const handleHeaderScrolled = () => {
    setTimeout(() => {
      setScrolledHeight(headerRef.current?.clientHeight || 0);
      setInitialHeight(headerRef.current?.clientHeight || 0);
    }, 300);
  };

  const handleHideHeader = useCallback(
    (value: boolean) => setHideHeader(value),
    []
  );

  useEffect(() => {
    setCssScrollBarWidthCustomVar();
  }, []);

  useEffect(() => {
    if (!deferHeaderForRoute) {
      setShouldRenderHeader(true);
      return;
    }

    setShouldRenderHeader(false);

    const showHeader = () => {
      setShouldRenderHeader(true);
    };
    const idleHandle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(showHeader, { timeout: 4000 })
        : window.setTimeout(showHeader, 1000);

    return () => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle as number);
      } else {
        clearTimeout(idleHandle as number);
      }
    };
  }, [deferHeaderForRoute, pathname]);

  useEffect(() => {
    if (!shouldRenderHeader) {
      return;
    }

    const initialHeightSetJob = setTimeout(
      () => setInitialHeight(headerRef.current?.clientHeight || 0),
      10
    );

    return () => {
      clearTimeout(initialHeightSetJob);
    };
  }, [id, pathname, shouldRenderHeader]);

  // Hide header when the user is open retreat from the App.
  useLayoutEffect(() => {
    if (isRetreatWebview) setHideHeader(true);
  }, [isRetreatWebview]);

  const isFluxNavigation = USE_FLUX_NAVIGATION && intlHeader == null;

  return (
    <HeaderComponentContext.Provider
      value={{
        initialHeight,
        scrolledHeight,
        stickyHeader: sticky,
        transparentHeader: subNavigationOpenId
          ? false
          : transparent && !sticky
          ? transparent
          : transparent && !scroll80,
        hideShadow: handleHideShadow,
        cookieBannerDisplay,
        subNavigationOpenId,
        isShowSearch,
        hideHeader,
        bgColor,
        showGetStarted,
        customCtaButton,
        useGreenCTA,
        setCookieBannerDisplay,
        setStickyHeader: handleSetSticky,
        setTransparentHeader: handleSetTransparent,
        setHideHeader: handleHideHeader,
        setSubNavigationOpenId,
        setIsShowSearch,
        setBgColor,
        setShowGetStarted,
        setCustomCtaButton,
        setUseGreenCTA
      }}
    >
      {!hideHeader && (
        <>
          {shouldRenderHeader && (
            <HeaderContainer
              // The flux bar is always fixed, like web-funnel-next's header;
              // setStickyHeader(false) only applies to the old header.
              sticky={
                isFluxNavigation || sticky || subNavigationOpenId !== undefined
              }
              id="site-header"
              ref={headerRef}
            >
              <Suspense fallback={<></>}>
                <Notification />
              </Suspense>

              {intlHeader != null && (
                <HeaderIntl
                  hideShadow={hideShadow}
                  onScrolled={handleHeaderScrolled}
                />
              )}

              {intlHeader == null && (
                <>
                  {isFluxNavigation ? (
                    <Suspense fallback={<></>}>
                      <FluxNavigation onScrolled={handleHeaderScrolled} />
                    </Suspense>
                  ) : (
                    <Header
                      hideShadow={hideShadow}
                      onScrolled={handleHeaderScrolled}
                    />
                  )}
                </>
              )}
            </HeaderContainer>
          )}

          {!transparent &&
            (isFluxNavigation ? (
              <Box
                data-testid="header-spacer"
                // Same scale as the flux bar, so the spacer matches its height
                // under the 0.8 zoom on #root (see index.css).
                className="no-global-zoom"
                flexShrink={0}
                width="100%"
                height={FLUX_SPACER_HEIGHT}
              />
            ) : (
              <HeaderSpace />
            ))}
        </>
      )}

      {children}
    </HeaderComponentContext.Provider>
  );
};

export const useHeader = () => useContext(HeaderComponentContext);
